// ─────────────────────────────────────────────
//  Room status taxonomy — single source of truth
//  Plan: active(green) / closing(orange) / cancel(purple), default ready.
//  Legacy data may use "in_progress"/"cancel"; we normalize on read.
// ─────────────────────────────────────────────

export const ROOM_STATUSES = ["empty", "ready", "active", "closing", "cancelled"];

// Display metadata. `text`/`pill`/`border`/`label` match how the board's
// RoomCard consumes them, so the card can read straight from here.
// Domain modules must not import the theme. Hex values here mirror the
// corresponding token values so visual reads stay consistent with the token theme.
// text "#4a5280" mirrors tokens.color.text.secondary (Plan 13 sync).
// pill/border alpha tints are light-theme equivalents replacing former dark-hex tints.
const META = {
  empty:     { label: "Empty",     text: "#4a5280", pill: "#e9eef5",   border: "#b9c6d8" },
  ready:     { label: "Ready",     text: "#1e40af", pill: "#e8edf9",   border: "#1e40af" },
  active:    { label: "Active",    text: "#10b981", pill: "#10b98120", border: "#10b981" },
  closing:   { label: "Closing",   text: "#f59e0b", pill: "#f59e0b20", border: "#f59e0b" },
  cancelled: { label: "Cancelled", text: "#a855f7", pill: "#a855f720", border: "#a855f7" },
};

// Map historical / shorthand values onto the canonical set.
const ALIASES = {
  in_progress: "active",
  inprogress: "active",
  cancel: "cancelled",
  canceled: "cancelled",
};

export function normalizeStatus(status) {
  if (!status) return "empty";
  const key = String(status).toLowerCase();
  return ALIASES[key] || key;
}

export function getStatusMeta(status) {
  return META[normalizeStatus(status)] || META.empty;
}

// Statuses a provider can set from the board/room editor (not "empty").
export const PROVIDER_STATUS_OPTIONS = ["ready", "active", "closing", "cancelled"];

// Statuses that count as a live/occupied room on the board.
export const ACTIVE_STATUSES = ["ready", "active", "closing"];

export const isActiveStatus = (status) =>
  ACTIVE_STATUSES.includes(normalizeStatus(status));

// ⚠️ A CANCELLED ROOM IS NOT RUNNING, AND THE STAFFING MATHS HAD NO WAY TO SAY
// SO. `buildTimeline` builds its lanes purely from assignment rows and never
// reads a room's status, so pressing Cancel changed the card's colour and
// nothing else: the room still counted toward `requiredDoctors`, and its
// supervising doctor still read as covering it.
//
// One definition, here, next to the statuses themselves — `domain/soloRelief`
// had its own private copy of exactly this set, which is how two answers to one
// question begin.
export const ENDED_STATUSES = ["cancelled"];

export const isEndedStatus = (status) =>
  ENDED_STATUSES.includes(normalizeStatus(status));

// The room ids a board should treat as no longer running. Pass the live room
// docs; anything unreadable is simply not included, because a room we cannot
// classify must not be silently removed from the staffing count.
export const endedRoomIds = (rooms = []) =>
  new Set((rooms || []).filter((r) => r && r.id && isEndedStatus(r.status)).map((r) => r.id));
